import { getAddress, getCreate2Address } from '@ethersproject/address'
import { pack, keccak256 } from '@ethersproject/solidity'
import { Pair, Token, WETH } from '@uniswap/sdk'
import { CHAIN_CONFIG } from './config.js'
import TOKEN_DEFAULTS from './constants/tokendefauts.json'

const config = CHAIN_CONFIG as any

function requiredString(value: any, name: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Missing chain config value: ${name}`)
  }
  return value.trim()
}

function configAddress(value: any, name: string): string {
  try {
    return getAddress(requiredString(value, name))
  } catch {
    throw new Error(`Invalid chain config address: ${name}`)
  }
}

export const SUPPORTED_CHAIN_ID: number = Number(config.chainId)
const configuredMode = typeof config.mode === 'string' ? config.mode.trim().toLowerCase() : 'native'
export const SWAP_MODE: 'native' | 'stable' = configuredMode === 'stable' ? 'stable' : 'native'
export const NATIVE_NAME: string = requiredString(config.native?.name, 'native.name')
export const NATIVE_SYMBOL: string = requiredString(config.native?.symbol, 'native.symbol')
export const NATIVE_DECIMALS: number = Number(config.native.decimals)

export const WRAPPED_NATIVE = new Token(
  SUPPORTED_CHAIN_ID as any,
  configAddress(config.weth?.address, 'weth.address'),
  Number(config.weth.decimals),
  requiredString(config.weth?.symbol, 'weth.symbol'),
  requiredString(config.weth?.name, 'weth.name')
)

export const STABLE_TOKEN: Token | undefined = config.stable
  ? new Token(
      SUPPORTED_CHAIN_ID as any,
      configAddress(config.stable?.address, 'stable.address'),
      Number(config.stable.decimals),
      requiredString(config.stable?.symbol, 'stable.symbol'),
      requiredString(config.stable?.name, 'stable.name')
    )
  : undefined

if (SWAP_MODE === 'stable' && !STABLE_TOKEN) {
  throw new Error('Missing chain config value: stable')
}

export const DEFAULT_SWAP_CURRENCY_ID: string = SWAP_MODE === 'stable' && STABLE_TOKEN ? STABLE_TOKEN.address : 'ETH'

export const MULTICALL_ADDRESS: string = configAddress(config.contracts?.multicall, 'contracts.multicall')
export const FACTORY_ADDRESS: string = configAddress(config.contracts?.factory, 'contracts.factory')
export const INIT_CODE_HASH: string = requiredString(config.contracts?.initCodeHash, 'contracts.initCodeHash')
export const ROUTER_ADDRESS: string = configAddress(config.contracts?.router, 'contracts.router')
export const EXPLORER_URL: string = requiredString(config.explorer?.url, 'explorer.url').replace(/\/+$/, '')
export const EXPLORER_NAME: string = requiredString(config.explorer?.name, 'explorer.name')
export const RPC_URL: string = requiredString(config.rpcUrl, 'rpcUrl')
export const CHAIN_HEX_ID = `0x${SUPPORTED_CHAIN_ID.toString(16)}`
export const WALLET_CHAIN_PARAMETERS = {
  chainId: CHAIN_HEX_ID,
  chainName: NATIVE_NAME,
  nativeCurrency: {
    name: NATIVE_NAME,
    symbol: NATIVE_SYMBOL,
    decimals: NATIVE_DECIMALS
  },
  rpcUrls: [RPC_URL],
  blockExplorerUrls: [EXPLORER_URL]
}
export const MENU_LINKS: { label: string; url: string; icon?: string }[] = Array.isArray(config.menuLinks)
  ? config.menuLinks.filter((item: any) => item?.label && item?.url)
  : []

export function getPairAddress(tokenA: Token, tokenB: Token): string {
  const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]
  return getCreate2Address(
    FACTORY_ADDRESS,
    keccak256(['bytes'], [pack(['address', 'address'], [token0.address, token1.address])]),
    INIT_CODE_HASH
  )
}

;(Pair as any).getAddress = getPairAddress
;(WETH as any)[SUPPORTED_CHAIN_ID] = WRAPPED_NATIVE

export const CONFIGURED_TOKENS: Token[] = (TOKEN_DEFAULTS as any).tokens
  .filter((token: any) => Number(token.chainId) === SUPPORTED_CHAIN_ID)
  .map((token: any) => {
    try {
      return new Token(
        Number(token.chainId) as any,
        getAddress(token.address),
        Number(token.decimals),
        token.symbol,
        token.name
      )
    } catch {
      console.warn(`Skipping invalid token default: ${token?.symbol ?? token?.address ?? 'unknown'}`)
      return undefined
    }
  })
  .filter((token: Token | undefined): token is Token => Boolean(token))

export const PRIMARY_BASES: Token[] = [WRAPPED_NATIVE, ...(STABLE_TOKEN ? [STABLE_TOKEN] : [])].filter(
  (token, index, tokens) => tokens.findIndex(other => other.equals(token)) === index
)

export const COMMON_BASES: Token[] = [...PRIMARY_BASES, ...CONFIGURED_TOKENS]
  .filter((token, index, tokens) => tokens.findIndex(other => other.equals(token)) === index)
